import { useCallback } from 'react'
import { Modifier } from '@dnd-kit/core'
import { useSelector } from 'react-redux'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { useTimelineMagnetize } from 'hooks/use-timeline-magnetize'
import { generateBreakpoints } from 'store/features/projectData/utils'
import { editorSelectors } from 'store/features/editor/selectors'
import { useScrollDelta } from 'hooks/useScrollDelta'
import { useTimelineContext } from 'context/TimelineContext'
import {
  getDraggedGroup,
  getGroupBounds,
  getHasOverlapDuringDrag,
  parseDropTarget
} from './utils'
import { IDndItem } from './types'

export const useCreateSnapModifier = (
  items: IDndItem[],
  selectedIds?: string[]
) => {
  const timelineItemsSortedByStartTime = useSelector(
    projectDataSelectors.timelineItemsSortedByStartTime
  )
  const timelineRulerSliceDuration = useSelector(
    editorSelectors.timelineRulerSliceDuration
  )
  const { magnetizeDrag, hideMagnetizingLine } = useTimelineMagnetize()
  const { scrollableContainerRef } = useTimelineContext()

  const { getScrollDelta, onDragStart } = useScrollDelta(scrollableContainerRef)

  const createSnapModifier = useCallback(
    (event: Parameters<Modifier>[0]) => {
      const { transform, active, over } = event

      const currentItem = active?.data.current as IDndItem

      if (!currentItem || !over) return transform

      const group = getDraggedGroup(items, currentItem.id, selectedIds)
      const groupIds = new Set(group.map(g => g.id))

      const itemsWithoutGroup = timelineItemsSortedByStartTime.filter(
        item => !groupIds.has(item.id)
      )

      const breakpoints = generateBreakpoints(
        itemsWithoutGroup,
        timelineRulerSliceDuration
      )

      const target = parseDropTarget(over)
      if (!target) return transform

      // Adjust transform.x for scroll delta ONLY for magnetizing calculations
      const scrollDelta = getScrollDelta()
      const adjustedTransformX = transform.x + scrollDelta

      const isOverlap =
        target.kind === 'gap'
          ? false
          : getHasOverlapDuringDrag({
              items,
              lineIndex: target.lineIndex,
              deltaX: adjustedTransformX,
              draggedItem: currentItem,
              group
            })
      if (isOverlap) {
        hideMagnetizingLine()
        return transform
      }

      // Magnetize against the group's outer leftmost / rightmost edges so the
      // whole group snaps as one and the snap line never appears inside it.

      const { groupLeftEdge, groupRightEdge } = getGroupBounds(group)

      const magnetizedDeltaX = magnetizeDrag(
        adjustedTransformX,
        groupLeftEdge,
        groupRightEdge,
        breakpoints
      )

      // Subtract scroll delta back from the final result to prevent double compensation
      const finalDeltaX = magnetizedDeltaX - scrollDelta

      return {
        ...transform,
        x: finalDeltaX
      }
    },
    [
      items,
      selectedIds,
      timelineItemsSortedByStartTime,
      timelineRulerSliceDuration,
      magnetizeDrag,
      hideMagnetizingLine,
      getScrollDelta
    ]
  )

  return { createSnapModifier, onDragStart, hideMagnetizingLine }
}
